import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Checkbox, Input, Modal, Spin, message } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { combiningObjects } from './listFormater';
import { UsersCardInnerContent } from './UsersCardInnerContent/UsersCardInnerContent';

import { createXlsxUserReport } from '../../../hooks/reports';
import { checkUser, getUsersDashboard } from '../../../hooks/users';
import { setSearchUser } from '../../../redux/actions/searchUser';
import { UserDashboardProps } from '../../../types/responses/UsersDashboard';
import { normalOrder, orderByCarName } from '../../../utils/columnHeaderOrder';
import { getProName } from '../../../utils/getProName/getProName';
import { setIconActive, usersListFilter } from '../../../utils/list';
import { modalErrorReport } from '../../../utils/modalErrorReport/modalErrorReport';
import { COLORS } from '../../../utils/theme/colors';
import { HeaderIcon } from '../../molecules/HeaderIcon/HeaderIcon';
import { ListSkeleton } from '../../molecules/ListSkeleton/ListSkeleton';
import CardListConstructor from '../CardListConstructor/CardListConstructor';
import { closeAllCards } from '../CardListConstructor/helpers';
import EmptySearch from '../EmptySearch';

const removeDuplicateUsers = (
  users: UserDashboardProps[],
): UserDashboardProps[] => {
  const uniqueUsers = new Map<number, UserDashboardProps>();

  users.forEach((user) => {
    if (!uniqueUsers.has(user.user_pk)) {
      uniqueUsers.set(user.user_pk, user);
    }
  });

  return Array.from(uniqueUsers.values());
};

export const UsersTable: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState<any>(false);
  const [usersDataList, setUsersDataList] = useState<UserDashboardProps[]>([]);
  const [data, setdata] = useState<any>({});
  const [paginationData, setPaginationData] = useState<any>([]);
  const [activeUsers, setActiveUsers] = useState(true);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [pagination, setPagination] = useState<any>();
  const [checkUserLoading, setCheckUserLoading] = useState(false);
  const childRef: any = useRef();

  const routes = useSelector((state: any) => state.user.routes);
  const paginate = () => childRef?.current?.paginate();

  const antLoadingIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const isVehicle = !!(
    routes && routes.find(({ path }) => path === '/vehicle/create')
  );

  const dispatch = useDispatch();
  const searchUser: any = useSelector(
    (state: any) => state.searchUser.searchUser,
  );
  const getUsers = useCallback(
    async (
      active: boolean = true,
      page = 1,
      size = 10,
      search: string,
      isVehicle: boolean = false,
    ) => {
      try {
        setLoading(true);

        const usersDashboard: any = await getUsersDashboard(
          active,
          page,
          size,
          search,
          isVehicle,
        );

        const normalizeUserDarshboardList =
          usersDashboard.userDashboardList.map((userList) => {
            return {
              ...userList,
              car_brand_name:
                userList.car_brand_name === null &&
                userList.car_model_name === null
                  ? 'Carro não cadastrado'
                  : userList.car_brand_name,
              car_model_name:
                userList.car_brand_name === null &&
                userList.car_model_name === null
                  ? ''
                  : userList.car_model_name,
            };
          });

        const uniqueUsers = removeDuplicateUsers(normalizeUserDarshboardList);
        setUsersDataList(uniqueUsers);

        const currentPagination = {
          page,
          total: usersDashboard.count,
          move: async (page) => {
            await getUsers(active, page, 10, search, isVehicle);

            setPagination({ ...currentPagination, page });
          },
        };

        setPagination(currentPagination);

        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    (async function () {
      await getUsers(activeUsers, 1, 10, searchUser, isVehicle);
    })();
  }, [activeUsers, searchUser, getUsers, isVehicle]);

  const HEADER = useMemo(() => {
    if (isVehicle) {
      return [
        {
          text: 'ID',
          sideIcon: <HeaderIcon />,
          action: (e: React.MouseEvent<HTMLDivElement>) =>
            normalOrder(
              'user_pk',
              usersListFilter('', usersDataList),
              (newList) => {
                setIconActive(e);
                closeAllCards();
                paginate();
                setdata(combiningObjects(newList, HEADER, isVehicle));
                setPaginationData(newList);
              },
              e,
            ),
          size: '8%',
          dropdown: <></>,
        },
        {
          text: T('Placa'),
          sideIcon: <HeaderIcon />,
          action: (e: React.MouseEvent<HTMLDivElement>) =>
            normalOrder(
              'user_name',
              usersListFilter('', usersDataList),
              (newList) => {
                setIconActive(e);
                closeAllCards();
                paginate();
                setdata(combiningObjects(newList, HEADER, isVehicle));
                setPaginationData(newList);
              },
              e,
            ),
          size: '25%',
          dropdown: <></>,
        },
        {
          text: T('Company'),
          sideIcon: <HeaderIcon />,
          action: (e: React.MouseEvent<HTMLDivElement>) =>
            normalOrder(
              'company_name',
              usersListFilter('', usersDataList),
              (newList) => {
                setIconActive(e);
                closeAllCards();
                paginate();
                setdata(combiningObjects(newList, HEADER, isVehicle));
                setPaginationData(newList);
              },
              e,
            ),
          size: '67%',
          dropdown: <></>,
        },
      ];
    }
    return [
      {
        text: 'ID',
        sideIcon: <HeaderIcon />,
        action: (e: React.MouseEvent<HTMLDivElement>) =>
          normalOrder(
            'user_pk',
            usersListFilter('', usersDataList),
            (newList) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER, isVehicle));
              setPaginationData(newList);
            },
            e,
          ),
        size: '8%',
        dropdown: <></>,
      },
      {
        text: T('Name'),
        sideIcon: <HeaderIcon />,
        action: (e: React.MouseEvent<HTMLDivElement>) =>
          normalOrder(
            'user_name',
            usersListFilter('', usersDataList),
            (newList) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER, isVehicle));
              setPaginationData(newList);
            },
            e,
          ),
        size: '30%',
        dropdown: <></>,
      },
      {
        text: T('Document'),
        sideIcon: <HeaderIcon />,
        action: (e: React.MouseEvent<HTMLDivElement>) =>
          normalOrder(
            'docNumber',
            usersListFilter('', usersDataList),
            (newList) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER, isVehicle));
              setPaginationData(newList);
            },
            e,
          ),
        size: '25%',
        dropdown: <></>,
      },
      {
        text: T('Car'),
        sideIcon: <HeaderIcon />,
        action: (e: React.MouseEvent<HTMLDivElement>) =>
          orderByCarName(
            usersListFilter('', usersDataList),
            (newList) => {
              setIconActive(e);
              closeAllCards();
              paginate();
              setdata(combiningObjects(newList, HEADER, isVehicle));
              setPaginationData(newList);
            },
            e,
          ),
        size: '37%',
        dropdown: <></>,
      },
    ];
  }, [isVehicle, usersDataList]);

  useEffect(() => {
    setdata(combiningObjects(usersDataList, HEADER, isVehicle));
    setPaginationData(usersDataList);
  }, [usersDataList, HEADER, isVehicle]);

  useEffect(() => {
    setPaginationData(usersListFilter('', usersDataList));
    setdata(
      combiningObjects(usersListFilter('', usersDataList), HEADER, isVehicle),
    );
  }, [searchUser, HEADER, usersDataList, isVehicle]);

  useEffect(() => {
    paginate();
  }, [paginationData]);

  const onChangeActiveUsers = (e: CheckboxChangeEvent) => {
    setActiveUsers(e.target.checked);
  };

  const handleDownloadExcel = async () => {
    setIsExcelLoading(true);

    const userForm = {
      activeUsers: activeUsers,
      searchInput: searchUser,
    };
    const xlsxUsersReport = await createXlsxUserReport(userForm);

    if (xlsxUsersReport.type === 'error') {
      if (xlsxUsersReport.status === 403) {
        modalErrorReport();
      } else {
        message.error(T('Unable to download the .xlsx file.'));
      }
    }
    if (xlsxUsersReport.status === 200) {
      message.success(
        T(
          'The report is being generated and will be sent to the email registered in the system.',
        ),
      );
    }

    setIsExcelLoading(false);
  };

  const handleSearch = (value: string) => {
    dispatch(setSearchUser(value));
  };

  const onCreateUser = async () => {
    setCheckUserLoading(true);

    const response: any = await checkUser();

    if (response.status === 403) {
      Modal.error({
        title: 'Limite de usuários atingido!',
        content: (
          <div style={{ fontWeight: 300 }}>
            Caso deseje adicionar mais usuários, será necessário fazer upgrade
            para a versão{' '}
            <strong style={{ fontWeight: 700 }}>{getProName()}</strong> ou
            excluir algum usuário.
          </div>
        ),
      });
    } else {
      history.push('/users/create');
    }

    setCheckUserLoading(false);
  };

  return (
    <Div>
      <div className='tools_bar'>
        <h1 className='page_title'>{isVehicle ? T('Vehicles') : T('Users')}</h1>
        <Checkbox
          checked={activeUsers}
          className='active_filter'
          onChange={onChangeActiveUsers}
        >
          {isVehicle ? T('Active Vehicles') : T('Active Users')}
        </Checkbox>

        <Input.Search
          placeholder={
            isVehicle
              ? T('Search by car license plate or company')
              : T('Search by name, email, document or phone')
          }
          onSearch={handleSearch}
          style={{ marginTop: 20, width: 350 }}
          size='large'
        />

        {isExcelLoading ? (
          <div style={{ margin: '10px 20px 0 50px' }}>
            <Spin indicator={antLoadingIcon} />
          </div>
        ) : (
          <button
            className='button-header cursor'
            onClick={handleDownloadExcel}
          >
            {T('Download Excel')}
          </button>
        )}

        {isVehicle ? (
          <button
            disabled={checkUserLoading}
            onClick={() => {
              history.push('/vehicle/create', {
                isVehicle: true,
              });
            }}
            className='button-header cursor'
          >
            {T('New vehicle')}
          </button>
        ) : routes && routes.find(({ path }) => path === '/users/create') ? (
          <button
            disabled={checkUserLoading}
            onClick={onCreateUser}
            className='button-header cursor'
          >
            {T('New user')}
          </button>
        ) : null}
      </div>
      {loading ? (
        <ListWrapper>
          <ListSkeleton style={{ paddingTop: 20 }} />
        </ListWrapper>
      ) : !loading && !data.cardsList?.length ? (
        <EmptySearch />
      ) : (
        <ListWrapper>
          <CardListConstructor
            childRef={childRef}
            data={data}
            InnerComponent={UsersCardInnerContent}
            paginationCallback={() => {}}
            pureData={usersListFilter('', paginationData)}
            riskText={!activeUsers}
            pagination={pagination}
          />
        </ListWrapper>
      )}
    </Div>
  );
};

const ListWrapper = styled.div`
  padding-left: 24px;
  padding-right: 24px;
`;

const Div = styled.div`
  padding-bottom: 60px;
  width: 100%;

  .header_item {
    font-size: 13px;
  }

  .card_header .card_value_label .main_card_text {
    font-size: 12px;
  }
  .thunder_icon {
    width: 20px;
    margin-right: 5px;
  }

  .chager_plug_icon {
    width: 20px;
    margin-right: 5px;
  }

  .tools_bar {
    display: flex;
    flex-direction: row;
    width: 98%;
    margin-right: 2%;
    align-items: center;
    justify-content: flex-end;

    .page_title {
      flex: auto;
      margin: 20px 0 0 2%;
      font-size: 21px;
      font-weight: normal;
      font-family: 'Roboto';
      color: #777;
    }
  }

  .search_input {
    width: 40%;
    margin-top: 23px;
  }

  .search_input input {
    box-shadow: 0 0 10px 1px rgba(0, 0, 0, 0.1);
    padding: 0 70px 0 30px;
    width: 100%;
    border-radius: 8px;
  }
  .button-header {
    background-color: ${COLORS.transparentDark};
    padding: 10px 30px;
    margin: 20px 0 0 30px;
    border-radius: 8px;
    font-size: 13px;
    color: #444;
    text-decoration: none;
    transition: background-color 200ms ease-in-out;

    :hover {
      background-color: ${({ theme }) => theme.hoveredDiv};
      color: ${COLORS.primary};
    }
  }

  .active_filter {
    margin: 20px 10px 0 0;
  }
`;
